import { ref, inject, provide } from 'vue'

// Toast symbol for dependency injection
const TOAST_KEY = Symbol('toast')

// Global toast store
const globalToasts = ref([])
const globalTimers = new Map()

// Create toast service
export function createToastService() {
  const toasts = ref([])
  const timers = new Map()

  const add = (toast) => {
    const id = Date.now() + Math.random()
    const newToast = {
      id,
      severity: toast.severity || 'info',
      summary: toast.summary || '',
      detail: toast.detail || '',
      life: toast.life !== undefined ? toast.life : 3000,
      closable: toast.closable !== false,
      createdAt: Date.now()
    }
    
    toasts.value.push(newToast)
    globalToasts.value.push(newToast)
    
    // Auto dismiss if life is set and greater than 0
    if (newToast.life && newToast.life > 0) {
      const timer = setTimeout(() => {
        remove(id)
      }, newToast.life)
      timers.set(id, timer)
      globalTimers.set(id, timer)
    }
    
    return id
  }

  const remove = (id) => {
    // Remove from local toasts
    const localIndex = toasts.value.findIndex(toast => toast.id === id)
    if (localIndex > -1) {
      toasts.value.splice(localIndex, 1)
    }
    
    // Remove from global toasts
    const globalIndex = globalToasts.value.findIndex(toast => toast.id === id)
    if (globalIndex > -1) {
      globalToasts.value.splice(globalIndex, 1)
    }
    
    // Clear timers
    if (timers.has(id)) {
      clearTimeout(timers.get(id))
      timers.delete(id)
    }
    if (globalTimers.has(id)) {
      clearTimeout(globalTimers.get(id))
      globalTimers.delete(id)
    }
  }

  const removeAll = () => {
    toasts.value = []
    globalToasts.value = []
    timers.forEach(timer => clearTimeout(timer))
    globalTimers.forEach(timer => clearTimeout(timer))
    timers.clear()
    globalTimers.clear()
  }

  const removeGroup = (group) => {
    const toRemove = toasts.value.filter(toast => toast.group === group)
    toRemove.forEach(toast => remove(toast.id))
  }

  return {
    toasts,
    add,
    remove,
    removeAll,
    removeGroup
  }
}

// Provide toast service
export function provideToast() {
  const toastService = createToastService()
  provide(TOAST_KEY, toastService)
  return toastService
}

// Use toast hook - PrimeVue compatible API
export function useToast() {
  // Try to inject from parent components first
  let toastService = inject(TOAST_KEY, null)
  
  // If no service provided, use global service
  if (!toastService) {
    toastService = {
      toasts: globalToasts,
      add: (toast) => {
        const id = Date.now() + Math.random()
        const newToast = {
          id,
          severity: toast.severity || 'info',
          summary: toast.summary || '',
          detail: toast.detail || '',
          life: toast.life !== undefined ? toast.life : 3000,
          closable: toast.closable !== false,
          createdAt: Date.now()
        }
        
        globalToasts.value.push(newToast)
        
        // Auto dismiss if life is set and greater than 0
        if (newToast.life && newToast.life > 0) {
          const timer = setTimeout(() => {
            toastService.remove(id)
          }, newToast.life)
          globalTimers.set(id, timer)
        }
        
        return id
      },
      remove: (id) => {
        const index = globalToasts.value.findIndex(toast => toast.id === id)
        if (index > -1) {
          globalToasts.value.splice(index, 1)
        }
        
        if (globalTimers.has(id)) {
          clearTimeout(globalTimers.get(id))
          globalTimers.delete(id)
        }
      },
      removeAll: () => {
        globalToasts.value = []
        globalTimers.forEach(timer => clearTimeout(timer))
        globalTimers.clear()
      },
      removeGroup: (group) => {
        const toRemove = globalToasts.value.filter(toast => toast.group === group)
        toRemove.forEach(toast => toastService.remove(toast.id))
      }
    }
  }

  return toastService
}

// Export global toasts for Toast component
export { globalToasts } 